import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ShuffleIcon } from '@phosphor-icons/react';
import { Num, Plural, T, useGT } from 'gt-react';
import {
  WORKBOOK_PREVIEW_ROWS,
  type WorkbookCatalogEntry,
  type WorkbookSample,
  type WorkbookSource,
  type WorkbookSourceSample,
} from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { sourceColumnTypes } from './workbookCatalog';
import { WorkbookSourcePreview } from './WorkbookSourcePreview';
import { WorkbookTable } from './WorkbookTable';
import { useSourcePreview } from './useSourcePreview';
import { reshuffled, sampleOf } from './worksheetSamples';
import {
  needsSourceSample,
  PREVIEW_ROW_CHOICES,
} from './workbookAddDataPreviewRows';

/** How the Add data preview can sample a Source: a date to sort by isn't known before it is read. */
const PREVIEW_SAMPLE_METHODS = [
  'first',
  'last',
  'random',
  'stratified',
] as const satisfies readonly WorkbookSample['method'][];

function SampleStatus({ data }: { data: WorkbookSourceSample }) {
  const shown = data.rows.length;
  return data.scannedAll ? (
    <T context="workbook add data preview: sample of a source read whole">
      Sample of <Num>{shown}</Num> of <Num>{data.scannedRows}</Num>{' '}
      <Plural n={data.scannedRows} one="row" other="rows" />
    </T>
  ) : (
    <T context="workbook add data preview: sample of a large source's leading rows">
      Sample of <Num>{shown}</Num> rows, taken from the first{' '}
      <Num>{data.scannedRows}</Num> rows
    </T>
  );
}

/** The rows the chosen sample reads, numbered by their place in the Source. */
function SampledRows({
  workbookId,
  source,
  sample,
  rows,
  types,
}: {
  workbookId: string;
  source: WorkbookSource;
  sample: WorkbookSample;
  rows: number;
  types: ReturnType<typeof sourceColumnTypes>;
}) {
  const query = useQuery({
    ...orpc.workbooks.sourceSample.queryOptions({
      input: { workbookId, source, sample, rows },
    }),
    // A new choice keeps the last sample in place while it is read.
    placeholderData: keepPreviousData,
  });
  const { data } = query;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <p
        role="status"
        className="min-h-4 text-xs text-muted-foreground tabular-nums"
      >
        {data && <SampleStatus data={data} />}
        {query.isFetching && (
          <>
            {data && ' · '}
            <T context="worksheet sample loading">Taking the sample…</T>
          </>
        )}
      </p>
      {query.error && (
        <p role="alert" className="text-xs text-destructive">
          {query.error.message}
        </p>
      )}
      {data && (
        <WorkbookTable
          table={data}
          rowNumbers={data.rowNumbers}
          types={types}
          fill
        />
      )}
    </div>
  );
}

function SampleControls({
  sample,
  columns,
  rows,
  onSample,
  onRows,
}: {
  sample: WorkbookSample;
  /** The Source's columns, once its preview is read. */
  columns: readonly string[];
  rows: number;
  onSample: (sample: WorkbookSample) => void;
  onRows: (rows: number) => void;
}) {
  const gt = useGT();
  const methods: Record<(typeof PREVIEW_SAMPLE_METHODS)[number], string> = {
    first: gt('First rows', { context: 'worksheet sample method' }),
    last: gt('Last rows', { context: 'worksheet sample method' }),
    random: gt('Random rows', { context: 'worksheet sample method' }),
    stratified: gt('Stratified by a column', {
      context: 'worksheet sample method',
    }),
  };
  return (
    <div
      role="group"
      aria-label={gt('Sample', { context: 'worksheet sample controls' })}
      className="flex flex-wrap items-center gap-1.5"
    >
      <Select
        value={String(rows)}
        onValueChange={(value) => onRows(Number(value))}
      >
        <SelectTrigger
          className="h-7 w-auto gap-1 text-xs"
          aria-label={gt('Rows to preview', {
            context: 'workbook add data preview row count',
          })}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PREVIEW_ROW_CHOICES.map((choice) => (
            <SelectItem key={choice} value={String(choice)}>
              <T context="workbook add data preview row count">
                <Num>{choice}</Num> rows
              </T>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={sample.method}
        onValueChange={(value) => {
          const method = PREVIEW_SAMPLE_METHODS.find((item) => item === value);
          if (method)
            onSample(sampleOf(method, sample, { any: columns, dates: [] }));
        }}
      >
        <SelectTrigger
          className="h-7 w-auto gap-1 text-xs"
          aria-label={gt('How the sample is taken', {
            context: 'worksheet sample method',
          })}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PREVIEW_SAMPLE_METHODS.flatMap((method) =>
            method === 'stratified' && columns.length === 0
              ? []
              : [
                  <SelectItem key={method} value={method}>
                    {methods[method]}
                  </SelectItem>,
                ],
          )}
        </SelectContent>
      </Select>
      {'column' in sample && (
        <Select
          value={sample.column}
          onValueChange={(column) => onSample({ ...sample, column })}
        >
          <SelectTrigger
            className="h-7 w-auto gap-1 text-xs"
            aria-label={gt('Sample column', {
              context: 'worksheet sample column',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {columns.map((column) => (
              <SelectItem key={column} value={column}>
                {column}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {'seed' in sample && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => onSample(reshuffled(sample))}
        >
          <ShuffleIcon />
          <T context="worksheet sample reshuffle action">New sample</T>
        </Button>
      )}
    </div>
  );
}

/**
 * The rows an item would read, before it is added: its first rows, or more of
 * them, or a sample (last, random, or stratified by a column) taken from its
 * leading rows. The table fills the height the pane leaves it.
 */
export function AddDataItemPreview({
  editor,
  source,
}: {
  /** The Workbook to preview with; null before it exists, which shows no preview. */
  editor: {
    workbookId: string | null;
    catalog: { data: readonly WorkbookCatalogEntry[] | undefined };
  };
  source: WorkbookSource;
}) {
  if (!editor.workbookId) return null;
  return (
    <ItemPreview
      workbookId={editor.workbookId}
      catalog={editor.catalog.data}
      source={source}
    />
  );
}

function ItemPreview({
  workbookId,
  catalog,
  source,
}: {
  workbookId: string;
  catalog: readonly WorkbookCatalogEntry[] | undefined;
  source: WorkbookSource;
}) {
  const [sample, setSample] = useState<WorkbookSample>({ method: 'first' });
  const [rows, setRows] = useState<number>(WORKBOOK_PREVIEW_ROWS);
  const table = useSourcePreview(workbookId, source);
  const types = sourceColumnTypes(source, catalog);
  return (
    <section className="flex min-h-[28rem] flex-1 flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          <T context="workbook add data detail: preview heading">Preview</T>
        </h3>
        <SampleControls
          sample={sample}
          columns={table.data?.columns ?? []}
          rows={rows}
          onSample={setSample}
          onRows={setRows}
        />
      </div>
      {needsSourceSample(sample, rows) ? (
        <SampledRows
          workbookId={workbookId}
          source={source}
          sample={sample}
          rows={rows}
          types={types}
        />
      ) : (
        <WorkbookSourcePreview
          data={table.data}
          error={table.error}
          pending={table.isFetching}
          types={types}
          fill
        />
      )}
    </section>
  );
}
